import type { TMessage } from 'src/types';

import { useMemo, useEffect } from 'react';

import { Stack, alpha, useTheme, Typography } from '@mui/material';

import { escapeHtml, sanitizeMessage } from 'src/utils/chat';

import { FileThumbnail } from 'src/components/file-thumbnail';

import FileName from './input/components/FileName';
import { useNotifyOverflowContext } from './hooks/useNotifyOverflowContext';

type ChatUserMessageProps = {
  data: TMessage;
};

function ChatUserMessage({ data: { content, type, allFiles }, ...others }: ChatUserMessageProps) {
  const theme = useTheme();
  const { notify } = useNotifyOverflowContext();
  const parsedContent = useMemo(() => escapeHtml(sanitizeMessage(content)), [content]);

  useEffect(() => {
    notify();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  return (
    <Stack
      p={1.5}
      spacing={1}
      bgcolor={theme.palette.mode === 'light' ? alpha(theme.palette.primary.main, 0.08) : theme.palette.background.paper}
      sx={{
        borderRadius: `${theme.shape.borderRadius}px`,
        wordWrap: 'break-word',
        width: 'fit-content',
      }}
      className="chat-message-list-item-text-message user-message"
      {...others}
    >
      {Array.isArray(allFiles) && allFiles?.length > 0 && (
        <Stack spacing={1}>
          {allFiles?.map(
            (fileItem, index) =>
              fileItem?.file.name && (
                <Stack key={index} direction="row" alignItems="center" spacing={1}>
                  <FileThumbnail file={fileItem.file.name} sx={{ width: 20, height: 20 }} />
                  <FileName name={fileItem.file.name} />
                </Stack>
              )
          )}
        </Stack>
      )}
      {parsedContent && (
        <Typography
          variant="body1"
          color="text.primary"
          dangerouslySetInnerHTML={{ __html: parsedContent }}
        />
      )}
    </Stack>
  );
}

export default ChatUserMessage;
